import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IRootState } from "../../app/store";

export interface IPnsSelectionState {
  selectedIds: string[];
  selectedParentIds: string[];
  expandedIds: string[];
}

const LOCAL_STORAGE_KEY = "pnsSelectionState";

const saveToLocalStorage = (state: IPnsSelectionState) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error saving PNS selection state to localStorage", error);
  }
};

let loadedInitialState: Partial<IPnsSelectionState> = {};
try {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored) {
    loadedInitialState = JSON.parse(stored);
  }
} catch (error) {
  console.error("Error loading PNS selection state from localStorage", error);
}

const initialState: IPnsSelectionState = {
  selectedIds: Array.isArray(loadedInitialState.selectedIds) ? loadedInitialState.selectedIds : [],
  selectedParentIds: Array.isArray(loadedInitialState.selectedParentIds)
    ? loadedInitialState.selectedParentIds
    : [],
  expandedIds: Array.isArray(loadedInitialState.expandedIds) ? loadedInitialState.expandedIds : [],
};

export const pnsSelectionSlice = createSlice({
  name: "pnsSelection",
  initialState,
  reducers: {
    setSelectedPnsIdsInRedux(state, action: PayloadAction<string[]>) {
      state.selectedIds = Array.from(new Set(action.payload));
      saveToLocalStorage(state);
    },
    setSelectedParentPnsIdsInRedux(state, action: PayloadAction<string[]>) {
      state.selectedParentIds = Array.from(new Set(action.payload));
      saveToLocalStorage(state);
    },
    addSelectedPnsIdInRedux(state, action: PayloadAction<string>) {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
        saveToLocalStorage(state);
      }
    },
    removeSelectedPnsIdInRedux(state, action: PayloadAction<string>) {
      state.selectedIds = state.selectedIds.filter((id) => id !== action.payload);
      saveToLocalStorage(state);
    },
    toggleSelectedPnsIdInRedux(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((x) => x !== id);
      } else {
        state.selectedIds.push(id);
      }
      saveToLocalStorage(state);
    },
    clearSelectedPnsIdsInRedux(state) {
      state.selectedIds = [];
      saveToLocalStorage(state);
    },
    setExpandedPnsIdsInRedux(state, action: PayloadAction<string[]>) {
      state.expandedIds = Array.from(new Set(action.payload));
      saveToLocalStorage(state);
    },
    toggleExpandedPnsIdInRedux(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.expandedIds.includes(id)) {
        state.expandedIds = state.expandedIds.filter((x) => x !== id);
      } else {
        state.expandedIds.push(id);
      }
      saveToLocalStorage(state);
    },
    clearExpandedPnsIdsInRedux(state) {
      state.expandedIds = [];
      saveToLocalStorage(state);
    },
  },
});

export const {
  setSelectedPnsIdsInRedux,
  setSelectedParentPnsIdsInRedux,
  addSelectedPnsIdInRedux,
  removeSelectedPnsIdInRedux,
  toggleSelectedPnsIdInRedux,
  clearSelectedPnsIdsInRedux,
  setExpandedPnsIdsInRedux,
  toggleExpandedPnsIdInRedux,
  clearExpandedPnsIdsInRedux,
} = pnsSelectionSlice.actions;

export const selectPnsSelectionState = (state: IRootState): IPnsSelectionState => state.pnsSelection;

export const selectSelectedPnsIds = (state: IRootState): string[] => state.pnsSelection.selectedIds;
export const selectSelectedParentPnsIds = (state: IRootState): string[] =>
  state.pnsSelection.selectedParentIds;
export const selectExpandedPnsIds = (state: IRootState): string[] => state.pnsSelection.expandedIds;

export default pnsSelectionSlice.reducer;
